import { useId } from 'react';
import styles from './BrandMark.module.css';

export interface BrandMarkProps {
  readonly size?: number | undefined;
  /** `full` adds the outer arc, inner rings and the red core of the hero logo. */
  readonly variant?: 'compact' | 'full' | undefined;
  /**
   * Draw the mark on mount rather than showing it complete. Reserved for the
   * hero — the one place the logo gets to *become* itself. Only the `full`
   * variant carries the choreography; anywhere else it is ignored.
   */
  readonly animated?: boolean | undefined;
  readonly title?: string | undefined;
  readonly className?: string | undefined;
}

/**
 * The platform's fingerprint mark — a thumbprint inside a gold ring, reading as
 * "leave your mark". Drawn inline so it inherits the theme's `currentColor`.
 */
export function BrandMark({
  size = 40,
  variant = 'compact',
  animated = false,
  title,
  className,
}: BrandMarkProps) {
  // `useId` keeps the mask id unique when several marks render on one page.
  const maskId = `brand-mask-${useId().replace(/:/g, '')}`;
  const isFull = variant === 'full';
  const stroke = isFull ? 1.7 : 3.4;
  // The draw only reads on the detailed hero mark; the compact monogram has too
  // few ridges for it to land, so it stays instant.
  const draw = animated && isFull;
  const svgClass = [className, draw ? styles.animate : ''].filter(Boolean).join(' ') || undefined;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      className={svgClass}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}

      <defs>
        <mask id={maskId}>
          {/* White keeps the thumb shape, black clips the ridges at the base. */}
          <path
            d="M60 24C77 24 88 39 88 58C88 79 76 94 60 94C44 94 32 79 32 58C32 39 43 24 60 24Z"
            fill="#fff"
          />
          <ellipse
            cx="59"
            cy={isFull ? 103 : 102}
            rx={isFull ? 31 : 30}
            ry={isFull ? 16 : 15}
            fill="#000"
          />
        </mask>
      </defs>

      <circle
        cx="60"
        cy="60"
        r="55"
        fill="none"
        stroke="var(--gold)"
        strokeWidth={isFull ? 0.9 : 1.6}
        className={draw ? styles.ring : undefined}
        pathLength={draw ? 1 : undefined}
      />

      {isFull ? (
        <path
          d="M104 44A46 46 0 0 1 104 76"
          fill="none"
          stroke="var(--gold)"
          strokeWidth="1.6"
          strokeLinecap="round"
          className={draw ? styles.arc : undefined}
          pathLength={draw ? 1 : undefined}
        />
      ) : null}

      <g
        mask={`url(#${maskId})`}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        className={draw ? styles.ridges : undefined}
      >
        {isFull ? (
          <>
            <ellipse cx="59" cy="57" rx="4" ry="5.5" pathLength={draw ? 1 : undefined} />
            <ellipse cx="58.4" cy="56" rx="8" ry="11" pathLength={draw ? 1 : undefined} />
            <ellipse cx="59.6" cy="55" rx="12" ry="17" pathLength={draw ? 1 : undefined} />
            <ellipse cx="58.6" cy="54" rx="16" ry="23" pathLength={draw ? 1 : undefined} />
            <ellipse cx="59.8" cy="53" rx="20" ry="29" pathLength={draw ? 1 : undefined} />
            <ellipse cx="58.8" cy="52" rx="24" ry="35" pathLength={draw ? 1 : undefined} />
            <ellipse cx="60" cy="51" rx="28" ry="41" pathLength={draw ? 1 : undefined} />
            <path
              d="M56 50C62 49 64 55 60 57"
              stroke="var(--accent)"
              strokeWidth="1.7"
              strokeLinecap="round"
              pathLength={draw ? 1 : undefined}
            />
          </>
        ) : (
          <>
            <ellipse cx="59" cy="56" rx="8" ry="11" />
            <ellipse cx="58" cy="54" rx="17" ry="24" />
            <ellipse cx="60" cy="52" rx="26" ry="37" />
          </>
        )}
      </g>

      {isFull ? (
        <g className={draw ? styles.flecks : undefined}>
          <circle cx="74" cy="95" r="1.9" fill="var(--gold)" />
          <circle cx="82" cy="91" r="1.5" fill="var(--gold)" />
          <circle cx="88" cy="85" r="1.2" fill="var(--gold)" />
        </g>
      ) : (
        <>
          <circle cx="78" cy="92" r="3.2" fill="var(--gold)" />
          <circle cx="88" cy="86" r="2.4" fill="var(--gold)" />
        </>
      )}
    </svg>
  );
}
